import React from 'react';
import { Box, Typography, Chip, Link, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ColumnConfigItem, CustomColumnDefinition } from '../../types/lead';
import { hasColumnData, DEFAULT_COLUMN_WIDTH, resolveOptionLabel, getCustomFieldValue } from './leadsUtils';
import { LeadSelectCell } from '../../components/leads/cells';
import { format12HourTime, formatCustomDate, formatCustomDateTime } from '../dateUtils';

const BOOLEAN_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const MultiSelectCell: React.FC<{ rawArr: any[]; options?: string[] }> = ({ rawArr, options = [] }) => {
  const getItemLabel = (v: any) => resolveOptionLabel(v, options);
  const maxVisible = 2;
  const visibleItems = rawArr.slice(0, maxVisible);
  const extraCount = rawArr.length - maxVisible;
  const fullText = rawArr.map(getItemLabel).join(', ');
  const chipMaxWidth = extraCount > 0 ? 70 : (rawArr.length > 1 ? 85 : 180);

  return (
    <Tooltip
      title={fullText}
      arrow
      placement="bottom-start"
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'window',
                padding: 12,
              },
            },
          ],
        },
        tooltip: {
          sx: {
            maxWidth: 300,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            p: 1,
          },
        },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'inline-flex',
          gap: 0.5,
          alignItems: 'center',
          overflow: 'hidden',
          py: 0.5,
          cursor: 'pointer',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        {visibleItems.map((v) => (
          <Chip
            key={String(v)}
            label={getItemLabel(v)}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              maxWidth: chipMaxWidth,
              flexShrink: 1,
              minWidth: 0,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 1,
              },
            }}
          />
        ))}
        {extraCount > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              fontSize: '0.75rem',
              ml: 0.25,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              userSelect: 'none',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            +{extraCount} more
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

interface RenderCustomColumnProps {
  col: ColumnConfigItem;
  customColumns: CustomColumnDefinition[];
  leads?: any[];
  handleCustomFieldChange?: (leadId: string, fieldKey: string, newValue: any) => void;
  onAddOptionToColumn?: (key: string, optionLabel: string) => Promise<any>;
}

export const getCustomColumnDef = ({
  col,
  customColumns,
  leads,
  handleCustomFieldChange,
  onAddOptionToColumn,
}: RenderCustomColumnProps): GridColDef => {
  const customDef = customColumns.find((c) => c.key === col.key);
  const colType = col.type || customDef?.type || 'text';
  const colOptions = col.options || customDef?.options || [];

  const hasData = hasColumnData(leads, col.key, true);

  let gridType: GridColDef['type'] = 'string';
  if (colType === 'number') {
    gridType = 'number';
  } else if (colType === 'date') {
    gridType = 'date';
  } else if (colType === 'datetime') {
    gridType = 'dateTime';
  } else if (colType === 'boolean') {
    gridType = 'boolean';
  } else if (colType === 'select') {
    gridType = 'singleSelect';
  }

  return {
    field: `custom_${col.key}`,
    colKey: col.key,
    headerName: col.label || customDef?.name || col.key,
    type: gridType,
    align: 'left',
    headerAlign: 'left',
    valueOptions: colType === 'select' ? colOptions : undefined,
    width: hasData ? (colType === 'textarea' || colType === 'multi_select' ? 220 : 160) : DEFAULT_COLUMN_WIDTH,
    valueGetter: (params: any) => {
      const rawVal = getCustomFieldValue(params.row.custom_fields || params.row.lead_metadata?.custom_fields, col.key);
      if (rawVal === undefined || rawVal === null || rawVal === '') {
        return null;
      }
      if (colType === 'boolean') {
        if (typeof rawVal === 'boolean') return rawVal;
        if (rawVal === 'true' || rawVal === '1' || rawVal === 1) return true;
        if (rawVal === 'false' || rawVal === '0' || rawVal === 0) return false;
        return null;
      }
      if (colType === 'number') {
        const num = Number(rawVal);
        return isNaN(num) ? null : num;
      }
      if (colType === 'date' || colType === 'datetime') {
        const date = new Date(rawVal);
        return isNaN(date.getTime()) ? null : date;
      }
      if (colType === 'time') {
        return String(rawVal);
      }
      if (colType === 'multi_select') {
        let arr: any[] = [];
        if (Array.isArray(rawVal)) {
          arr = rawVal;
        } else if (typeof rawVal === 'string' && rawVal.trim().startsWith('[') && rawVal.trim().endsWith(']')) {
          try {
            const parsed = JSON.parse(rawVal);
            if (Array.isArray(parsed)) arr = parsed;
            else arr = [parsed];
          } catch (e) {
            arr = rawVal.split(',').map((s) => s.trim());
          }
        } else if (typeof rawVal === 'string') {
          arr = rawVal.split(',').map((s) => s.trim());
        } else {
          arr = [rawVal];
        }
        return arr.map((v) => resolveOptionLabel(v, colOptions)).join(', ');
      }
      return String(rawVal);
    },
    renderCell: (params: any) => {
      const val = params.value;

      if (colType === 'boolean' && handleCustomFieldChange) {
        return (
          <LeadSelectCell
            value={val}
            options={BOOLEAN_OPTIONS}
            color={val ? 'primary' : 'default'}
            onChange={(newVal) => handleCustomFieldChange(params.row.id, col.key, newVal)}
            emitStringValue
          />
        );
      }

      if (colType === 'select' && handleCustomFieldChange) {
        const rawVal = getCustomFieldValue(params.row.custom_fields || params.row.lead_metadata?.custom_fields, col.key);
        return (
          <LeadSelectCell
            value={rawVal ?? val}
            options={colOptions}
            color="default"
            onChange={(newVal) => handleCustomFieldChange(params.row.id, col.key, newVal)}
            onAddOption={onAddOptionToColumn ? (newOpt) => onAddOptionToColumn(col.key, newOpt) : undefined}
          />
        );
      }

      if (colType === 'multi_select' && handleCustomFieldChange) {
        const rawArr = getCustomFieldValue(params.row.custom_fields || params.row.lead_metadata?.custom_fields, col.key);
        return (
          <LeadSelectCell
            multiple
            value={rawArr ?? val}
            options={colOptions}
            onChange={(newVal) => handleCustomFieldChange(params.row.id, col.key, newVal)}
            onAddOption={onAddOptionToColumn ? (newOpt) => onAddOptionToColumn(col.key, newOpt) : undefined}
          />
        );
      }

      if (val === undefined || val === null || (Array.isArray(val) && val.length === 0) || val === '') {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
      }

      if (colType === 'boolean') {
        return (
          <Chip
            label={val ? 'Yes' : 'No'}
            size="small"
            color={val ? 'primary' : 'default'}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        );
      }

      if (colType === 'date') {
        return <Typography variant="body2" color="text.secondary">{formatCustomDate(val)}</Typography>;
      }

      if (colType === 'datetime') {
        return <Typography variant="body2" color="text.secondary">{formatCustomDateTime(val)}</Typography>;
      }

      if (colType === 'time') {
        return <Typography variant="body2" color="text.secondary">{format12HourTime(val)}</Typography>;
      }

      if (colType === 'email') {
        return (
          <Tooltip title={String(val)}>
            <Link
              href={`mailto:${val}`}
              underline="hover"
              variant="body2"
              noWrap
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {String(val)}
            </Link>
          </Tooltip>
        );
      }

      if (colType === 'phone') {
        return (
          <Tooltip title={String(val)}>
            <Link
              href={`tel:${val}`}
              underline="hover"
              variant="body2"
              noWrap
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {String(val)}
            </Link>
          </Tooltip>
        );
      }

      if (colType === 'url') {
        return (
          <Tooltip title={String(val)}>
            <Link
              href={String(val)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body2"
              noWrap
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {String(val)}
            </Link>
          </Tooltip>
        );
      }

      if (colType === 'select') {
        return <Chip label={resolveOptionLabel(val, colOptions)} size="small" variant="outlined" />;
      }

      if (colType === 'multi_select') {
        const rawArr = params.row.custom_fields?.[col.key];
        if (Array.isArray(rawArr) && rawArr.length > 0) {
          return <MultiSelectCell rawArr={rawArr} options={colOptions} />;
        }
        return <Typography variant="body2" color="text.secondary">{String(val)}</Typography>;
      }

      return (
        <Tooltip title={String(val)}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {String(val)}
          </Typography>
        </Tooltip>
      );
    },
  } as any;
};
